<script setup lang="ts">
import { computed, ref } from 'vue';

import type {
  IFixedTrackerPaymentsComposableOptions,
  IFixedTrackerPaymentsForm,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/fixed-tracker-payments/types';
import type { VFormRef } from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import {
  useFixedTrackerPaymentsForm,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/fixed-tracker-payments/composables';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { assert, isArrayOf, isDate } from '@utils/guards';

import type { IBulkChangingFixedPaymentEmits, IBulkChangingFixedPaymentProps } from './types';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS } from '../../constants';
import { useChangeTrackerFixedPaymentsMutation } from '../../mutations';

const {
  selectedIds = [],
  filterId = 0,
  page = 1,
  affId,
} = defineProps<IBulkChangingFixedPaymentProps>();

const emit = defineEmits<IBulkChangingFixedPaymentEmits>();

const visible = defineModel<boolean>('visible');

const { formatMonthDate } = useFormatMonthDate();

const form = ref<VFormRef>();

const {
  mutateAsync,
  isLoading,
  errors,
  clearError,
  clearAllErrors,
} = useChangeTrackerFixedPaymentsMutation();

const formOptions = computed<IFixedTrackerPaymentsComposableOptions>(() => ({
  hideSerialId: true,
  datePickerSelectionMode: 'multiple',
}));

const { formSchema, formGroups } = useFixedTrackerPaymentsForm(formOptions);

function onFormSubmit(): void {
  form.value?.submit();
}

async function onSubmit({ payment_size, dates, description }: IFixedTrackerPaymentsForm): Promise<void> {
  assert(isArrayOf(isDate, dates));
  await mutateAsync({
    id: affId,
    history_of_fixed_payments_changes: {
      payment_size,
      description,
      dates: formatMonthDate(dates),
      segment_id: filterId,
      selected_tracker_ids: selectedIds,
      page,
    },
  });

  close();
}

function onHide(): void {
  clearAllErrors();
  emit('hide');
}

function close(): void {
  visible.value = false;
}
</script>

<template>
  <VDialog
    v-model:visible="visible"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_CHANGING_FIXED_PAYMENT_TITLE')"
    @hide="onHide"
  >
    <VForm
      ref="form"
      :schema="formSchema"
      :groups="formGroups"
      :is-loading="isLoading"
      :custom-errors="errors"
      @submit="onSubmit"
      @interacted="clearError"
    />
    <template #footer>
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_CHANGING_FIXED_PAYMENT_CANCEL')"
        outlined
        @click="close"
      />
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_CHANGING_FIXED_PAYMENT_SAVE')"
        :loading="isLoading"
        @click="onFormSubmit"
      />
    </template>
  </VDialog>
</template>
